import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  AfterViewInit,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';

// services
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from '@app/profile/services/profile.service';
import { ContentCardService } from './../../../shared/components/content/content-card/content-card.service';

// types

import { ModalService } from '@dg/shared-services';
import { UserExperienceDetailsModalComponent } from '@app/profile/components/user-experience-details-modal/user-experience-details-modal.component';
import { ProfileExperiencesService } from '@app/profile/components/profile-overview/experiences/profile-experiences.service';
import { Profile } from '@app/profile/profile.model';
import { DatePipe } from '@angular/common';
import { ProfileCollectionItem } from '@app/profile/profile-api.model';
import { InputDetails } from '@app/inputs/inputs-api.model';

@Component({
  selector: 'dgx-user-experience-tile',
  templateUrl: './user-experience-tile.component.html',
  styleUrls: ['./user-experience-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserExperienceTile
  extends SubscriberBaseDirective
  implements OnInit, AfterViewInit
{
  @Input() public userExperience: ProfileCollectionItem<InputDetails>;
  @Input() public owner: Profile;
  @Input() public isSelected: boolean;
  @Input() public hideMenu: boolean;
  @Input() public expandCard?: boolean;
  @Input() public tileIndex?: number;
  @Input() public lazyChunkIndex?: number;
  @Output()
  public clickedTitleEllipses: EventEmitter<number[]> = new EventEmitter<
    number[]
  >();

  @ViewChild('title') public title: ElementRef<HTMLElement>;

  public showEllipsis: boolean;
  public userExperienceContent: InputDetails;

  public authUser = this.authService.authUser;

  public i18n = this.translateService.instant([
    'Core_ExperienceDisplayName',
    'Core_ViewDetails',
    'ProfileCtrl_ExperienceDatePresent',
    'A11y_ExpandForTitle',
    'Core_JobRoleLiteral',
    'Opportunities_TypeProject',
    'Opportunities_TypeMentorship',
    'Opportunities_TypeMenteeship',
    'Opportunities_TypeShadowing',
    'Opportunities_TypeStretchAssignment',
    'Opportunities_TypeOther',
  ]);

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private profileService: ProfileService,
    private modalService: ModalService,
    private profileExperiencesService: ProfileExperiencesService,
    private datePipe: DatePipe,
    private contentCardService: ContentCardService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  public get isLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  public get ownerIsViewing(): boolean {
    return this.profileService.ownerIsViewing;
  }

  public get formattedDateRange(): string {
    const [startDate, endDate] =
      this.userExperienceContent.dateRange.split(' - ');
    const formattedStartDate = this.datePipe.transform(
      `1 ` + startDate,
      'MMM yyyy'
    );
    let formattedEndDate = this.i18n.ProfileCtrl_ExperienceDatePresent;
    if (endDate !== 'Present') {
      formattedEndDate = this.datePipe.transform(`1 ` + endDate, 'MMM yyyy');
    }
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  public get getResourceLabel() {
    const {
      content: { inputSubType },
    } = this.userExperience;

    switch (inputSubType) {
      case 'JobRole':
        return this.i18n.Core_JobRoleLiteral;
      case 'Project':
        return this.i18n.Opportunities_TypeProject;
      case 'Mentorship':
        return this.i18n.Opportunities_TypeMentorship;
      case 'Menteeship':
        return this.i18n.Opportunities_TypeMenteeship;
      case 'Shadowing':
        return this.i18n.Opportunities_TypeShadowing;
      case 'StretchAssignment':
        return this.i18n.Opportunities_TypeStretchAssignment;
      case 'Other':
        return this.i18n.Opportunities_TypeOther;
      default:
        return this.i18n.Core_ExperienceDisplayName;
    }
  }

  public ngOnInit() {
    this.userExperienceContent = this.userExperience.content as InputDetails;
  }

  public ngAfterViewInit(): void {
    this.showEllipsis =
      this.title.nativeElement.clientHeight <
      this.title.nativeElement.scrollHeight;
    this.cdr.detectChanges();
  }

  public showDetails(): void {
    this.profileExperiencesService
      .getExperienceDetails(
        this.userExperienceContent.userInputId,
        this.owner.guid
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe((userExperience) => {
        const userExperienceItem = {
          content: userExperience,
        };
        this.modalService
          .show<void>(UserExperienceDetailsModalComponent, {
            inputs: { userExperience: userExperienceItem },
          })
          .pipe(this.takeUntilDestroyed())
          .subscribe();
      });
  }

  public onEllipsisClick(titleElement) {
    let newIndexesOfCardsToExpand;
    [this.showEllipsis, newIndexesOfCardsToExpand] =
      this.contentCardService.expandCard(
        titleElement,
        this.title,
        this.tileIndex,
        this.lazyChunkIndex
      );
    this.clickedTitleEllipses.emit(newIndexesOfCardsToExpand);
  }
}
